import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Eye, EyeOff, Pencil, Trash2, ExternalLink, X, Calendar, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CmsEvenements({ navigate }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', event_date: '', event_time: '19:30', section: 'restaurant', published: true });

  const load = () => fetch(`${API}/api/site/evenements/all`).then(r => r.json()).then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ title: '', description: '', event_date: '', event_time: '19:30', section: 'restaurant', published: true });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title || !form.event_date) return;
    if (editing) {
      await fetch(`${API}/api/site/evenements/${editing}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
    } else {
      await fetch(`${API}/api/site/evenements`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
    }
    resetForm();
    load();
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title, description: item.description || '',
      event_date: item.event_date || '', event_time: item.event_time || '19:30',
      section: item.section || 'restaurant', published: item.published !== false
    });
    setEditing(item.id);
    setShowForm(true);
  };

  const togglePublished = async (item) => {
    await fetch(`${API}/api/site/evenements/${item.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ published: !item.published })
    });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet evenement ?')) return;
    await fetch(`${API}/api/site/evenements/${id}`, { method: 'DELETE' });
    load();
  };

  const isPast = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div data-testid="cms-evenements">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('dashboard')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm" data-testid="evenements-back-btn">
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="flex items-center gap-2">
            <span className="text-gray-300">|</span>
            <h1 className="text-lg font-bold text-gray-900">Evenements</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/site/restaurant" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
            <ExternalLink size={14} /> Voir le site
          </a>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600"
            data-testid="new-evenement-btn">
            <Plus size={16} /> Nouvel evenement
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6 space-y-4" data-testid="evenement-form">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">{editing ? 'Modifier' : 'Nouvel evenement'}</h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <input type="text" placeholder="Titre de l'evenement" value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" data-testid="evenement-title-input" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Date</label>
              <input type="date" value={form.event_date}
                onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" data-testid="evenement-date-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Heure</label>
              <input type="time" value={form.event_time}
                onChange={e => setForm(p => ({ ...p, event_time: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" data-testid="evenement-time-input" />
            </div>
          </div>
          <textarea placeholder="Description de l'evenement..." rows={3} value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none" data-testid="evenement-desc-input" />
          <div className="flex items-center gap-3">
            <button onClick={handleSave} className="bg-[#1a2744] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#243559]" data-testid="save-evenement-btn">
              {editing ? 'Enregistrer' : 'Publier'}
            </button>
            <button onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700">Annuler</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {items.map(item => {
          const past = isPast(item.event_date);
          return (
            <div key={item.id}
              className={`bg-white border rounded-lg p-5 ${past ? 'border-gray-200' : 'border-l-4 border-l-purple-500 border-gray-200'}`}
              data-testid={`evenement-${item.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    {past && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">Passe</span>}
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                    {item.event_date && (
                      <span className="flex items-center gap-1">
                        <Calendar size={13} /> {formatDate(item.event_date)}
                      </span>
                    )}
                    {item.event_time && (
                      <span className="flex items-center gap-1">
                        <Clock size={13} /> {item.event_time}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => togglePublished(item)} className="p-2 rounded-lg hover:bg-gray-100" data-testid={`toggle-evt-${item.id}`}>
                    {item.published ? <Eye size={18} className="text-green-500" /> : <EyeOff size={18} className="text-gray-400" />}
                  </button>
                  <button onClick={() => handleEdit(item)} className="p-2 rounded-lg hover:bg-gray-100" data-testid={`edit-evt-${item.id}`}>
                    <Pencil size={18} className="text-blue-500" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 rounded-lg hover:bg-red-50" data-testid={`delete-evt-${item.id}`}>
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {items.length === 0 && <p className="text-center text-sm text-gray-400 py-12">Aucun evenement</p>}
      </div>
    </div>
  );
}
